import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import L from 'leaflet'
import { Marker, Pane, Polygon, Polyline, useMap, useMapEvents } from 'react-leaflet'
import { ArrowUpRight, Check, ChevronDown, ChevronUp, Circle as KreisIcon, Copy, Crosshair, Hexagon, MapPin, Ruler, Trash2, Undo2, X } from 'lucide-react'
import {
  koordDezimal, koordGradMinuten, koordMgrs, koordUtm, himmelsrichtung, kreis as kreisRing, linienzug, polygon as polygonMass,
  strecke as streckeMass, textFlaeche, textGrad, textLaenge, type Pkt,
} from './geodaesie'

/*
 * Messwerkzeuge für Leaflet-Karten (Taktische Karte der Führung, Co-Pilot-Karte): Strecke, Fläche, Kreis/Radius, Peilung, Koordinate.
 * Rechnet geodätisch auf dem WGS84-Ellipsoid (geographiclib, siehe geodaesie.ts). Messungen sind nur lokal und temporär (kein Speichern).
 *
 * Einbau in eine Seite:
 *   const messen = useMessen()                                  // Zustand
 *   <MessLayer messen={messen} />                               // INNERHALB von <MapContainer>
 *   Panel „Messen“:  <MessPanelInhalt messen={messen} einklappen={...} />
 *   Klick-Handler der Seite:  if (messenAktiv(e.target)) return  // Karte reagiert nicht auf Klicks, solange gemessen wird
 * Bedienung (Maus und Touch): Punkte per Klick/Tipp setzen, Abschließen per Knopf (kein Doppelklick), Punkte lassen sich ziehen,
 * Escape bricht die laufende Messung ab (zweimal: Werkzeug beenden). Solange ein Werkzeug aktiv ist, sind Marker, Flächen und
 * Popups der Karte nicht anklickbar (CSS-Klasse am Kartencontainer), damit Tipps nur Messpunkte setzen.
 */

export type MessArt = 'strecke' | 'flaeche' | 'kreis' | 'peilung' | 'punkt'

interface Messung { id: number; art: MessArt; punkte: Pkt[] }

const ARTEN: Array<{ art: MessArt; titel: string; kurz: string; hinweis: string; icon: ReactNode }> = [
  { art: 'strecke', titel: 'Strecke', kurz: 'Strecke', hinweis: 'Punkte antippen. Gesamt- und Teilstrecken. „Abschließen“ beendet die Linie.', icon: <Ruler className="h-4 w-4" /> },
  { art: 'flaeche', titel: 'Fläche', kurz: 'Fläche', hinweis: 'Eckpunkte antippen (mindestens 3). Fläche und Umfang. „Abschließen“ schließt das Polygon.', icon: <Hexagon className="h-4 w-4" /> },
  { art: 'kreis', titel: 'Kreis / Radius', kurz: 'Kreis', hinweis: 'Mittelpunkt antippen, dann einen Punkt auf dem Kreis.', icon: <KreisIcon className="h-4 w-4" /> },
  { art: 'peilung', titel: 'Peilung', kurz: 'Peilung', hinweis: 'Startpunkt antippen, dann den Zielpunkt. Richtung in Grad und Himmelsrichtung.', icon: <ArrowUpRight className="h-4 w-4" /> },
  { art: 'punkt', titel: 'Koordinate', kurz: 'Punkt', hinweis: 'Punkt antippen: Koordinate in WGS84, Grad/Minuten, UTM und MGRS (kopierbar).', icon: <MapPin className="h-4 w-4" /> },
]

/** Wie viele Punkte schließen die Messung von selbst ab (0 = nur per Knopf) */
const AUTO_FERTIG: Record<MessArt, number> = { strecke: 0, flaeche: 0, kreis: 2, peilung: 2, punkt: 1 }
const MIN_PUNKTE: Record<MessArt, number> = { strecke: 2, flaeche: 3, kreis: 2, peilung: 2, punkt: 1 }
const FARBE = '#22d3ee'

export interface MessenZustand {
  art: MessArt | null
  punkte: Pkt[]
  fertig: boolean
  /** frühere, bereits abgeschlossene Messungen (bleiben sichtbar, bis „Alle löschen“) */
  messungen: Messung[]
  waehle: (art: MessArt | null) => void
  addPunkt: (p: Pkt) => void
  setPunkt: (i: number, p: Pkt) => void
  rueckgaengig: () => void
  abschliessen: () => void
  /** Escape: laufende Messung verwerfen; ohne laufende Messung das Werkzeug beenden */
  abbrechen: () => void
  alleLoeschen: () => void
  beenden: () => void
}

export function useMessen(): MessenZustand {
  const [art, setArt] = useState<MessArt | null>(null)
  const [punkte, setPunkte] = useState<Pkt[]>([])
  const [fertig, setFertig] = useState(false)
  const [messungen, setMessungen] = useState<Messung[]>([])
  const zaehler = useRef(1)

  const archivieren = useCallback((a: MessArt | null, p: Pkt[], f: boolean) => {
    if (a && f && p.length >= MIN_PUNKTE[a]) setMessungen(m => [...m, { id: zaehler.current++, art: a, punkte: p }])
  }, [])

  return useMemo<MessenZustand>(() => ({
    art, punkte, fertig, messungen,
    waehle: neu => { archivieren(art, punkte, fertig); setArt(neu); setPunkte([]); setFertig(false) },
    addPunkt: p => {
      if (!art) return
      let basis = punkte
      if (fertig) { archivieren(art, punkte, true); basis = [] }
      const neu = [...basis, p]
      setPunkte(neu)
      setFertig(AUTO_FERTIG[art] > 0 && neu.length >= AUTO_FERTIG[art])
    },
    setPunkt: (i, p) => setPunkte(ps => ps.map((q, k) => (k === i ? p : q))),
    rueckgaengig: () => {
      if (!art) return
      if (fertig && AUTO_FERTIG[art] === 0) { setFertig(false); return }
      setPunkte(ps => ps.slice(0, -1)); setFertig(false)
    },
    abschliessen: () => { if (art && punkte.length >= MIN_PUNKTE[art]) setFertig(true) },
    abbrechen: () => {
      if (punkte.length > 0) { setPunkte([]); setFertig(false) } else setArt(null)
    },
    alleLoeschen: () => { setPunkte([]); setFertig(false); setMessungen([]) },
    beenden: () => { archivieren(art, punkte, fertig); setArt(null); setPunkte([]); setFertig(false) },
  }), [art, punkte, fertig, messungen, archivieren])
}

/** Misst die Karte gerade? (für eigene Klick-Handler der Seite: dann nichts tun) */
export function messenAktiv(map: L.Map | null | undefined): boolean {
  return !!map && map.getContainer().classList.contains('ph-messen-aktiv')
}

// --- Ergebnis -------------------------------------------------------------------------------------------------------
interface Zeile { label: string; wert: string; kopie?: string }

function ergebnis(art: MessArt, p: Pkt[], fertig: boolean): { kurz: string; zeilen: Zeile[] } {
  const koord = (q: Pkt, i: number): Zeile => ({ label: `Punkt ${i + 1}`, wert: koordDezimal(q), kopie: koordDezimal(q) })
  const punkteZeilen = p.slice(0, 12).map(koord)
  if (art === 'punkt') {
    if (!p.length) return { kurz: 'Punkt antippen', zeilen: [] }
    const q = p[0]
    return {
      kurz: koordDezimal(q),
      zeilen: [
        { label: 'WGS84 dezimal', wert: koordDezimal(q), kopie: koordDezimal(q) },
        { label: 'Grad/Minuten', wert: koordGradMinuten(q), kopie: koordGradMinuten(q) },
        { label: 'UTM', wert: koordUtm(q), kopie: koordUtm(q) },
        { label: 'MGRS', wert: koordMgrs(q), kopie: koordMgrs(q) },
      ],
    }
  }
  if (art === 'strecke') {
    if (p.length < 2) return { kurz: p.length ? 'Nächsten Punkt antippen' : 'Startpunkt antippen', zeilen: punkteZeilen }
    const l = linienzug(p)
    return {
      kurz: `Gesamt ${textLaenge(l.gesamt)}${fertig ? '' : ' (laufend)'}`,
      zeilen: [
        { label: 'Gesamtstrecke', wert: textLaenge(l.gesamt), kopie: textLaenge(l.gesamt) },
        ...l.teile.slice(0, 30).map((m, i): Zeile => ({ label: `Teilstrecke ${i + 1}`, wert: textLaenge(m) })),
        ...punkteZeilen,
      ],
    }
  }
  if (art === 'flaeche') {
    if (p.length < 3) return { kurz: p.length ? `${p.length} Punkt${p.length > 1 ? 'e' : ''} – mindestens 3 nötig` : 'Eckpunkte antippen', zeilen: punkteZeilen }
    const m = polygonMass(p)
    const f = textFlaeche(m.flaeche)
    return {
      kurz: `${f.kurz} · Umfang ${textLaenge(m.umfang)}`,
      zeilen: [
        { label: 'Fläche', wert: f.alle, kopie: f.alle },
        { label: 'Umfang', wert: textLaenge(m.umfang), kopie: textLaenge(m.umfang) },
        ...punkteZeilen,
      ],
    }
  }
  if (art === 'kreis') {
    if (p.length < 2) return { kurz: p.length ? 'Punkt auf dem Kreis antippen' : 'Mittelpunkt antippen', zeilen: punkteZeilen }
    const r = streckeMass(p[0], p[1]).m
    const f = textFlaeche(Math.PI * r * r)
    return {
      kurz: `Radius ${textLaenge(r)} · ${f.kurz}`,
      zeilen: [
        { label: 'Radius', wert: textLaenge(r), kopie: textLaenge(r) },
        { label: 'Durchmesser', wert: textLaenge(2 * r) },
        { label: 'Umfang', wert: textLaenge(2 * Math.PI * r) },
        { label: 'Fläche', wert: f.alle },
        { label: 'Mittelpunkt', wert: koordDezimal(p[0]), kopie: koordDezimal(p[0]) },
      ],
    }
  }
  // Peilung
  if (p.length < 2) return { kurz: p.length ? 'Zielpunkt antippen' : 'Startpunkt antippen', zeilen: punkteZeilen }
  const s = streckeMass(p[0], p[1])
  const txt = `${textGrad(s.peilung)} ${himmelsrichtung(s.peilung)}`
  return {
    kurz: `${txt} · ${textLaenge(s.m)}`,
    zeilen: [
      { label: 'Peilung', wert: txt, kopie: textGrad(s.peilung) },
      { label: 'Entfernung', wert: textLaenge(s.m), kopie: textLaenge(s.m) },
      { label: 'Rückpeilung', wert: `${textGrad(s.rueckpeilung)} ${himmelsrichtung(s.rueckpeilung)}` },
      { label: 'Start', wert: koordDezimal(p[0]), kopie: koordDezimal(p[0]) },
      { label: 'Ziel', wert: koordDezimal(p[1]), kopie: koordDezimal(p[1]) },
    ],
  }
}

async function kopiere(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true } catch { /* Rückfall unten (z. B. ohne Berechtigung) */ }
  try {
    const t = document.createElement('textarea')
    t.value = text; t.style.position = 'fixed'; t.style.opacity = '0'
    document.body.appendChild(t); t.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(t)
    return ok
  } catch { return false }
}

// --- Darstellung auf der Karte ------------------------------------------------------------------------------------------
const STIL_ID = 'ph-messen-stil'
const CSS = `
.ph-messen-aktiv{cursor:crosshair !important}
.ph-messen-aktiv .leaflet-marker-pane *,.ph-messen-aktiv .leaflet-overlay-pane *,.ph-messen-aktiv .leaflet-shadow-pane *,
.ph-messen-aktiv .leaflet-tooltip-pane *,.ph-messen-aktiv .leaflet-popup-pane *{pointer-events:none !important}
.ph-messen-aktiv .leaflet-grab{cursor:crosshair}
.ph-mess-pkt{width:22px;height:22px;border-radius:9999px;background:#0e7490;border:3px solid #fff;box-shadow:0 0 0 2px ${FARBE},0 1px 4px rgba(0,0,0,.6);box-sizing:border-box}
.ph-mess-pkt.erster{background:#fff;border-color:#0e7490}
.ph-mess-label{display:inline-block;transform:translate(-50%,-135%);white-space:nowrap;background:rgba(8,47,73,.92);color:#fff;font:600 11px/1.2 system-ui,sans-serif;padding:2px 6px;border-radius:6px;border:1px solid ${FARBE};pointer-events:none}
`
function nutzeStil() {
  useEffect(() => {
    if (document.getElementById(STIL_ID)) return
    const el = document.createElement('style'); el.id = STIL_ID; el.textContent = CSS
    document.head.appendChild(el)
  }, [])
}

const punktIcon = (erster: boolean) => L.divIcon({ html: '', className: `ph-mess-pkt${erster ? ' erster' : ''}`, iconSize: [22, 22], iconAnchor: [11, 11] })
const labelIcon = (text: string) => L.divIcon({ html: `<span class="ph-mess-label">${text.replace(/</g, '&lt;')}</span>`, className: '', iconSize: [0, 0] })

function mitte(a: Pkt, b: Pkt): Pkt { return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] }

function Zeichnung({ art, punkte, fertig, bearbeitbar, onZiehen }: {
  art: MessArt; punkte: Pkt[]; fertig: boolean; bearbeitbar: boolean; onZiehen?: (i: number, p: Pkt) => void
}) {
  const opt = { color: FARBE, weight: 3, opacity: 0.95, pane: 'messen' } as const
  const labels: Array<{ p: Pkt; t: string }> = []
  let formen: ReactNode = null
  if (art === 'strecke' && punkte.length >= 2) {
    const l = linienzug(punkte)
    formen = <Polyline positions={punkte} pathOptions={opt} interactive={false} />
    if (punkte.length <= 14) l.teile.forEach((m, i) => { if (punkte.length > 2) labels.push({ p: mitte(punkte[i], punkte[i + 1]), t: textLaenge(m) }) })
    labels.push({ p: punkte[punkte.length - 1], t: `Σ ${textLaenge(l.gesamt)}` })
  } else if (art === 'flaeche' && punkte.length >= 2) {
    formen = punkte.length >= 3 && fertig
      ? <Polygon positions={punkte} pathOptions={{ ...opt, fillColor: FARBE, fillOpacity: 0.18 }} interactive={false} />
      : <Polyline positions={punkte.length >= 3 ? [...punkte, punkte[0]] : punkte} pathOptions={{ ...opt, dashArray: fertig ? undefined : '6 6' }} interactive={false} />
    if (punkte.length >= 3) { const m = polygonMass(punkte); labels.push({ p: punkte[0], t: `${textFlaeche(m.flaeche).kurz}` }) }
  } else if (art === 'kreis' && punkte.length >= 2) {
    const r = streckeMass(punkte[0], punkte[1]).m
    formen = (
      <>
        <Polygon positions={kreisRing(punkte[0], r)} pathOptions={{ ...opt, fillColor: FARBE, fillOpacity: 0.12 }} interactive={false} />
        <Polyline positions={[punkte[0], punkte[1]]} pathOptions={{ ...opt, weight: 2, dashArray: '5 5' }} interactive={false} />
      </>
    )
    labels.push({ p: mitte(punkte[0], punkte[1]), t: `r = ${textLaenge(r)}` })
  } else if (art === 'peilung' && punkte.length >= 2) {
    const s = streckeMass(punkte[0], punkte[1])
    formen = <Polyline positions={[punkte[0], punkte[1]]} pathOptions={opt} interactive={false} />
    labels.push({ p: mitte(punkte[0], punkte[1]), t: `${textGrad(s.peilung)} ${himmelsrichtung(s.peilung)} · ${textLaenge(s.m)}` })
  }
  return (
    <>
      {formen}
      {labels.map((l, i) => <Marker key={`l${i}`} position={l.p} icon={labelIcon(l.t)} interactive={false} keyboard={false} pane="messen" />)}
      {punkte.map((p, i) => (
        <Marker key={`p${i}`} position={p} icon={punktIcon(i === 0)} draggable={bearbeitbar} keyboard={false} pane="messen"
          eventHandlers={bearbeitbar && onZiehen ? { dragend: e => { const ll = (e.target as L.Marker).getLatLng(); onZiehen(i, [ll.lat, ll.lng]) } } : undefined} />
      ))}
    </>
  )
}

/** Innerhalb von <MapContainer>: Klicks, Zeichnung und Messleiste. */
export function MessLayer({ messen }: { messen: MessenZustand }) {
  nutzeStil()
  const map = useMap()
  const aktiv = messen.art !== null
  const leiste = useRef<HTMLDivElement | null>(null)

  // Karte in den Messmodus schalten: CSS-Klasse (Marker/Flächen nicht anklickbar), Doppelklick-Zoom aus, Popups zu
  useEffect(() => {
    const c = map.getContainer()
    if (!aktiv) return
    c.classList.add('ph-messen-aktiv')
    map.closePopup()
    const dk = map.doubleClickZoom.enabled()
    if (dk) map.doubleClickZoom.disable()
    return () => { c.classList.remove('ph-messen-aktiv'); if (dk) map.doubleClickZoom.enable() }
  }, [map, aktiv])

  useMapEvents({ click: e => { if (aktiv) messen.addPunkt([e.latlng.lat, e.latlng.lng]) } })

  // Escape
  useEffect(() => {
    if (!aktiv) return
    const f = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); messen.abbrechen() } }
    window.addEventListener('keydown', f)
    return () => window.removeEventListener('keydown', f)
  }, [aktiv, messen])

  const leisteRef = useCallback((el: HTMLDivElement | null) => {
    leiste.current = el
    if (el) { L.DomEvent.disableClickPropagation(el); L.DomEvent.disableScrollPropagation(el) }
  }, [])

  return (
    <>
      <Pane name="messen" style={{ zIndex: 650 }}>
        {messen.messungen.map(m => <Zeichnung key={m.id} art={m.art} punkte={m.punkte} fertig bearbeitbar={false} />)}
        {messen.art && <Zeichnung art={messen.art} punkte={messen.punkte} fertig={messen.fertig} bearbeitbar onZiehen={messen.setPunkt} />}
      </Pane>
      {aktiv && createPortal(<div ref={leisteRef}><Messleiste messen={messen} /></div>, map.getContainer())}
    </>
  )
}

function KopierKnopf({ text }: { text: string }) {
  const [ok, setOk] = useState(false)
  return (
    <button type="button" aria-label="Kopieren" title="Kopieren"
      onClick={async () => { if (await kopiere(text)) { setOk(true); setTimeout(() => setOk(false), 1500) } }}
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-dark-border text-dark-text-muted hover:bg-dark-bg-tertiary">
      {ok ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
    </button>
  )
}

const KNOPF = 'flex min-h-[44px] min-w-[44px] items-center justify-center gap-1.5 rounded-lg border px-3 text-xs font-semibold'

/** Schwebende Leiste auf der Karte: Werkzeugwahl, Ergebnis, Rückgängig/Abschließen/Löschen/Beenden. */
function Messleiste({ messen }: { messen: MessenZustand }) {
  const [details, setDetails] = useState(false)
  const art = messen.art!
  const info = ARTEN.find(a => a.art === art)!
  const erg = ergebnis(art, messen.punkte, messen.fertig)
  const kannAbschliessen = AUTO_FERTIG[art] === 0 && !messen.fertig && messen.punkte.length >= MIN_PUNKTE[art]
  const letztes = erg.zeilen.find(z => z.kopie)
  return (
    <div role="region" aria-label="Messwerkzeug"
      style={{ position: 'absolute', zIndex: 1000 }}
      className="left-3 right-16 top-3 sm:left-1/2 sm:right-auto sm:top-auto sm:bottom-3 sm:w-[min(540px,calc(100%-9rem))] sm:-translate-x-1/2">
      <div className="rounded-xl border border-dark-border bg-dark-bg-secondary/95 p-2 text-dark-text shadow-2xl backdrop-blur">
        <div className="flex gap-1 overflow-x-auto" role="toolbar" aria-label="Messart">
          {ARTEN.map(a => (
            <button key={a.art} type="button" aria-pressed={a.art === art} onClick={() => messen.waehle(a.art)} title={a.titel}
              className={`${KNOPF} shrink-0 ${a.art === art ? 'border-primary-500 bg-primary-500/20 text-dark-text' : 'border-dark-border text-dark-text-muted'}`}>
              {a.icon}<span className="hidden sm:inline">{a.kurz}</span>
            </button>
          ))}
          <button type="button" onClick={messen.beenden} aria-label="Messen beenden" title="Messen beenden"
            className={`${KNOPF} ml-auto shrink-0 border-dark-border text-dark-text-muted`}><X className="h-4 w-4" /></button>
        </div>
        <div className="mt-2 flex items-start gap-2 px-1">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-cyan-300" aria-live="polite">{erg.kurz}</div>
            {messen.punkte.length === 0 && <div className="text-xs text-dark-text-muted">{info.hinweis}</div>}
          </div>
          {letztes && <KopierKnopf text={letztes.kopie!} />}
          {erg.zeilen.length > 0 && (
            <button type="button" onClick={() => setDetails(d => !d)} aria-expanded={details} aria-label="Einzelwerte"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-dark-border text-dark-text-muted">
              {details ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
          )}
        </div>
        {details && erg.zeilen.length > 0 && (
          <ul className="mt-2 max-h-[min(30dvh,220px)] space-y-1 overflow-y-auto overscroll-contain border-t border-dark-border pt-2 text-xs">
            {erg.zeilen.map((z, i) => (
              <li key={i} className="flex items-center gap-2 px-1">
                <span className="w-28 shrink-0 text-dark-text-muted">{z.label}</span>
                <span className="min-w-0 flex-1 break-words font-mono text-dark-text">{z.wert}</span>
                {z.kopie && <KopierKnopf text={z.kopie} />}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-2 flex flex-wrap gap-1">
          <button type="button" onClick={messen.rueckgaengig} disabled={messen.punkte.length === 0}
            className={`${KNOPF} border-dark-border text-dark-text disabled:opacity-40`}><Undo2 className="h-4 w-4" />Zurück</button>
          {kannAbschliessen && (
            <button type="button" onClick={messen.abschliessen}
              className={`${KNOPF} border-primary-500 bg-primary-500/20 text-dark-text`}><Check className="h-4 w-4" />Abschließen</button>
          )}
          <button type="button" onClick={messen.alleLoeschen} disabled={messen.punkte.length === 0 && messen.messungen.length === 0} aria-label="Alle löschen"
            className={`${KNOPF} border-dark-border text-dark-text disabled:opacity-40`}><Trash2 className="h-4 w-4" /><span className="hidden sm:inline">Alle löschen</span></button>
        </div>
      </div>
    </div>
  )
}

/** Inhalt des Kartenpanels „Messen“ (außerhalb der Karte): Werkzeug wählen, danach erscheint die Leiste auf der Karte. */
export function MessPanelInhalt({ messen, einklappen }: { messen: MessenZustand; einklappen?: () => void }) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-dark-text-muted">Messungen sind nur lokal und temporär (werden nicht gespeichert). Solange ein Werkzeug aktiv ist, reagieren Marker und Flächen der Karte nicht auf Tippen.</p>
      <div className="grid grid-cols-2 gap-2">
        {ARTEN.map(a => (
          <button key={a.art} type="button" aria-pressed={messen.art === a.art}
            onClick={() => { messen.waehle(messen.art === a.art ? null : a.art); if (messen.art !== a.art) einklappen?.() }}
            className={`${KNOPF} justify-start ${messen.art === a.art ? 'border-primary-500 bg-primary-500/20 text-dark-text' : 'border-dark-border text-dark-text-muted'}`}>
            {a.icon}{a.titel}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        {messen.art && <button type="button" onClick={messen.beenden} className={`${KNOPF} flex-1 border-dark-border text-dark-text`}><Crosshair className="h-4 w-4" />Messen beenden</button>}
        {(messen.messungen.length > 0 || messen.punkte.length > 0) && (
          <button type="button" onClick={messen.alleLoeschen} className={`${KNOPF} flex-1 border-dark-border text-dark-text`}><Trash2 className="h-4 w-4" />Alle löschen</button>
        )}
      </div>
    </div>
  )
}
